/**
 * Facilitator panel: inputs excluded from later AI aggregates, with include-anyway toggle.
 * Used on ReportGenScreen (/reportgen).
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Panel = styled.section`
  margin-bottom: 32px;
  padding: 16px 20px;
  background: rgba(255,255,255,0.04);
  border: 1px solid #222;
  border-radius: 10px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const Title = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: #ccc;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0;
`;

const Hint = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0 0 12px;
`;

const Count = styled.p`
  font-size: 13px;
  color: #fbbf24;
  margin: 0 0 12px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(0,0,0,0.25);
  border: 1px solid #2a2a2a;
  border-radius: 8px;
`;

const Meta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemTitle = styled.span`
  font-size: 14px;
  color: #fff;
  word-break: break-word;
`;

const ItemDetail = styled.span`
  font-size: 12px;
  color: #888;
`;

const ToggleBtn = styled.button`
  flex-shrink: 0;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => p.$on ? 'rgba(0,255,136,0.35)' : '#444'};
  background: ${p => p.$on ? 'rgba(0,255,136,0.12)' : 'rgba(255,255,255,0.06)'};
  color: ${p => p.$on ? '#00ff88' : '#ccc'};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Empty = styled.p`
  font-size: 13px;
  color: #555;
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: 13px;
  color: #ff6666;
  margin: 0;
`;

export default function ExcludedInputsPanel({ instanceId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!instanceId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(API_ENDPOINTS.INPUT_EXCLUSIONS(instanceId), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('load failed');
      setData(await res.json());
    } catch (err) {
      console.error('Failed to load input exclusions:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (item) => {
    if (!instanceId || busyId) return;
    const include = item.override?.include !== true;
    setBusyId(item.recordId);
    try {
      const res = await fetch(
        API_ENDPOINTS.INPUT_EXCLUSION(instanceId, item.stage, item.recordId),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ include }),
        },
      );
      if (!res.ok) throw new Error('save failed');
      await load();
    } catch (err) {
      console.error('Failed to save exclusion override:', err);
      setError(true);
    } finally {
      setBusyId(null);
    }
  }, [instanceId, busyId, load]);

  if (!instanceId) return null;

  const codeLabel = (code) => {
    if (!code) return t('reportGen.exclusions.forced');
    const key = `reportGen.exclusions.codes.${code}`;
    const label = t(key);
    return label === key ? code : label;
  };

  const stageLabel = (stage) => {
    const key = `reportGen.exclusions.stages.${stage}`;
    const label = t(key);
    return label === key ? stage : label;
  };

  return (
    <Panel>
      <Header>
        <AlertTriangle size={14} color="#fbbf24" />
        <Title>{t('reportGen.exclusions.title')}</Title>
      </Header>
      <Hint>{t('reportGen.exclusions.hint')}</Hint>

      {loading && !data && <Loader2 size={16} color="#888" />}
      {error && <ErrorText>{t('reportGen.exclusions.loadFailed')}</ErrorText>}

      {data && data.total === 0 && (!data.items || data.items.length === 0) && (
        <Empty>{t('reportGen.exclusions.empty')}</Empty>
      )}

      {data && (data.total > 0 || (data.items && data.items.length > 0)) && (
        <>
          <Count>{t('reportGen.exclusions.count', { count: data.total })}</Count>
          <List>
            {(data.items || []).map((item) => {
              const included = item.override?.include === true;
              return (
                <Row key={`${item.stage}:${item.recordId}`}>
                  <Meta>
                    <ItemTitle>{item.title || '—'}</ItemTitle>
                    <ItemDetail>
                      {stageLabel(item.stage)}
                      {' · '}
                      {codeLabel(item.code)}
                      {item.message ? ` · ${item.message}` : ''}
                    </ItemDetail>
                  </Meta>
                  <ToggleBtn
                    type="button"
                    $on={included}
                    disabled={busyId === item.recordId}
                    onClick={() => toggle(item)}
                  >
                    {included
                      ? t('reportGen.exclusions.included')
                      : t('reportGen.exclusions.includeAnyway')}
                  </ToggleBtn>
                </Row>
              );
            })}
          </List>
        </>
      )}
    </Panel>
  );
}
